import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as reviewApi from '@/api/review';

// ═══════════════════════════════════════════════════════════════════
// QUERY KEYS
// ═══════════════════════════════════════════════════════════════════

export const reviewKeys = {
  all: ['review'] as const,
  queue: (page?: number) => [...reviewKeys.all, 'queue', page] as const,
  total: () => [...reviewKeys.all, 'total'] as const,
  job: (jobId: string | null) => [...reviewKeys.all, 'job', jobId] as const,
};

// ═══════════════════════════════════════════════════════════════════
// QUERIES
// ═══════════════════════════════════════════════════════════════════

/** Refresh cadence while any row still has unfinished mockup renders. */
export const REVIEW_QUEUE_POLL_INTERVAL_MS = 5000;

/**
 * Review queue with automatic refresh while any row still has
 * PENDING/PROCESSING mockups: the thumbnails keep coming in and the row
 * becomes approvable on its own once every render is done.
 */
export function useReviewQueue(page = 1) {
  return useQuery({
    queryKey: reviewKeys.queue(page),
    queryFn: () => reviewApi.getReviewQueue(page),
    refetchInterval: (query) => {
      const hasPending = query.state.data?.data.some(
        (item) => item.mockupSummary.pending > 0
      );
      return hasPending ? REVIEW_QUEUE_POLL_INTERVAL_MS : false;
    },
  });
}

/**
 * Only the size of the review queue, for the seller shell's badge. Reads
 * `meta.total` from a one-item page so the shell never carries the rows;
 * sits under `reviewKeys.all`, so every review action refreshes it too.
 */
export function useReviewQueueTotal() {
  return useQuery({
    queryKey: reviewKeys.total(),
    queryFn: () => reviewApi.getReviewQueue(1, 1),
    select: (data) => data.meta.total,
  });
}

/** Report page size per request (server cap per page). */
const JOB_REPORT_PAGE_SIZE = 50;

/**
 * Defensive bound on cursor-following (batches are capped at 100
 * items, i.e. 2 pages) so a server-side cursor bug can never spin
 * the client into an endless fetch loop.
 */
const MAX_JOB_REPORT_PAGES = 10;

/**
 * Polls a batch publish job every 2s until it reaches a terminal
 * state; the report items fill the result table incrementally.
 *
 * While the job is RUNNING only the first page is shown (incremental
 * fill — the next poll refreshes it anyway). Once the job is terminal
 * the remaining pages are fetched via nextCursor so batches larger
 * than one page render their full report.
 */
export function useReviewJob(jobId: string | null) {
  return useQuery({
    queryKey: reviewKeys.job(jobId),
    queryFn: async (): Promise<reviewApi.ReviewJobStatus> => {
      const first = await reviewApi.getReviewJob(
        jobId as string,
        undefined,
        JOB_REPORT_PAGE_SIZE
      );
      const { status } = first.job;
      if (status === 'QUEUED' || status === 'RUNNING') {
        return first;
      }
      let page = first;
      const items = [...first.items];
      let fetched = 1;
      while (page.nextCursor !== null && fetched < MAX_JOB_REPORT_PAGES) {
        page = await reviewApi.getReviewJob(
          jobId as string,
          page.nextCursor,
          JOB_REPORT_PAGE_SIZE
        );
        items.push(...page.items);
        fetched += 1;
      }
      return { ...first, items, nextCursor: page.nextCursor };
    },
    enabled: jobId !== null,
    refetchInterval: (query) => {
      const status = query.state.data?.job.status;
      return status === 'QUEUED' || status === 'RUNNING' || status === undefined
        ? 2000
        : false;
    },
  });
}

// ═══════════════════════════════════════════════════════════════════
// MUTATIONS
// ═══════════════════════════════════════════════════════════════════

function useReviewAction(
  actionFn: (designIds: string[]) => Promise<reviewApi.ReviewActionResponse>
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (designIds: string[]) => actionFn(designIds),
    onSettled: () => {
      // Success AND failure (e.g. 409 conflict) refresh the queue so
      // rows reflect what actually happened elsewhere.
      queryClient.invalidateQueries({ queryKey: reviewKeys.all });
      queryClient.invalidateQueries({ queryKey: ['designs'] });
    },
  });
}

export function useApproveReviewDesigns() {
  return useReviewAction(reviewApi.approveReviewDesigns);
}

export function useDiscardReviewDesigns() {
  return useReviewAction(reviewApi.discardReviewDesigns);
}

export function useRetryReviewDesigns() {
  return useReviewAction(reviewApi.retryReviewDesigns);
}
